import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { ArrowDownward, Mail, Smartphone, VolumeUp, SvgIconComponent } from '@mui/icons-material';
import parsePhoneNumberFromString from 'libphonenumber-js/mobile';
import { ActionsButton } from '../../../actions-button/actions-button.component';
import { LoadedActionsButtonItem } from '../../../actions-button/actions-button.types';
import { StatusBadge } from '../../../status-badge/status-badge.component';
import { useUserDetailsPageHeaderStyles } from './user-details-page-header.styles';
import { PadLock } from '../../../icons/PadLock.icon';
import { SVGIconComponent } from '../../../icons/types';

export interface UserDetailsPageHeaderProps {
  name: string;
  groupName: string;
  address?: { line1?: string; line2?: string };
  status?: {
    label: string;
    Icon: SvgIconComponent;
    color: string;
  };
  mobile?: string;
  email?: string;
  prefersLowerLockers?: boolean;
  prefersAudioPrompts?: boolean;
  actions: LoadedActionsButtonItem[];
  managedByAccount?: { id?: string; name?: string };
  externalID?: string;
  transactionTypesForUser: { Icon: SVGIconComponent; label: string }[];
}

export function UserDetailsPageHeader({
  name,
  groupName,
  address,
  status,
  email,
  mobile,
  prefersLowerLockers,
  prefersAudioPrompts,
  actions,
  managedByAccount,
  externalID,
  transactionTypesForUser
}: UserDetailsPageHeaderProps) {
  let phoneNumber: string | undefined = mobile;
  if (phoneNumber) {
    try {
      phoneNumber = parsePhoneNumberFromString(phoneNumber)?.formatInternational();
    } catch (e) {
    } finally {
      if (phoneNumber === undefined) {
        phoneNumber = mobile;
      }
    }
  }
  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useUserDetailsPageHeaderStyles();
  return (
    <Grid container sx={{ flexDirection: isSM ? 'column' : 'row' }} className={classes.root}>
      <Grid
        container
        size={{
          md: 4
        }}
        sx={{
          justifyContent: 'space-between',
          flexDirection: 'column'
        }}
      >
        <Grid container sx={{ flexDirection: 'column' }}>
          <Grid className={classes.userName}>
            <h2>{name}</h2>
            {managedByAccount && isSM ? <PadLock className={classes.padLockMobile} /> : null}
          </Grid>
          <Grid>
            <p className={classes.subtitle}>{groupName}</p>
          </Grid>
        </Grid>
        {address ? (
          <Grid container sx={{ flexDirection: 'column' }} className={classes.addressBox}>
            <Grid>{address.line1 ? <p>{address.line1}</p> : null}</Grid>
            <Grid>{address.line2 ? <p>{address.line2}</p> : null}</Grid>
          </Grid>
        ) : null}
      </Grid>
      <Grid
        container
        spacing={2}
        size={{
          md: 4
        }}
        sx={{
          justifyContent: 'space-between',
          flexDirection: 'column'
        }}
      >
        <Grid>
          {status ? (
            <StatusBadge
              color={status.color}
              label={status.label}
              cornerRounding="max"
              Icon={status.Icon}
              iconSize="18px"
            />
          ) : null}
        </Grid>
        <Grid container direction="row" spacing={4}>
          <Grid>
            <Grid className={classes.capabilities}>Capabilities</Grid>
            <Grid container direction="row" spacing={1}>
              {transactionTypesForUser.length === 0 ? (
                <Grid>
                  <span>No capabilities</span>
                </Grid>
              ) : null}
              {transactionTypesForUser.map((type) => (
                <Grid key={type.label}>
                  <type.Icon className={classes.capabilitiesIcon} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
        <Grid container>
          <Grid>
            {prefersLowerLockers ? (
              <p className={classes.info}>
                <ArrowDownward /> prefers low lockers
              </p>
            ) : null}
            {prefersAudioPrompts ? (
              <p className={classes.info}>
                <VolumeUp /> prefers audio guidance
              </p>
            ) : null}
            {/* A contact row the user hasn't got is dropped entirely rather than rendered as a
                bare icon with nothing beside it. */}
            {phoneNumber ? (
              <p className={classes.info}>
                <Smartphone /> {phoneNumber}
              </p>
            ) : null}
            {email ? (
              <p className={classes.info}>
                <Mail /> {email}
              </p>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        size={{
          md: 4
        }}
        sx={{
          justifyContent: 'space-between',
          flexDirection: 'column'
        }}
      >
        {/* ActionsButton renders nothing when the array is empty. */}
        <Grid container direction="row" spacing={2}>
          <div className={classes.actionButtonBox}>
            <ActionsButton itemArray={actions} />
          </div>
        </Grid>

        {(!managedByAccount && externalID && !isSM) || (externalID && isSM) ? (
          <Grid>
            <p className={classes.info}>ID: {externalID}</p>
          </Grid>
        ) : null}

        {managedByAccount && !isSM ? (
          <Grid container direction="row" className={classes.managedUser} spacing={2}>
            <Grid
              sx={{ flexDirection: 'column' }}
              spacing={2}
              size={{
                md: 11
              }}
            >
              <p className={classes.info}>Managed by {managedByAccount.name}</p>
              <p className={classes.info}>ID: {externalID}</p>
            </Grid>
            <Grid
              sx={{ flexDirection: 'column' }}
              size={{
                md: 1
              }}
            >
              <PadLock className={classes.padLock} />
            </Grid>
          </Grid>
        ) : null}
      </Grid>
    </Grid>
  );
}
